/*
 * Formatação para leitura de operador.
 *
 * Tudo em pt-BR, e tudo tolerante a dado ausente: a API omite campo vazio
 * (`omitempty` no Go), e uma tela que quebra ao encontrar `undefined` quebra
 * exatamente no boleto que o operador foi investigar.
 */

/**
 * Dinheiro é **inteiro em centavos** em todo o produto — nunca decimal.
 *
 * A divisão por 100 acontece só aqui, na borda de apresentação. Fazê-la mais
 * cedo introduziria ponto flutuante no caminho do dado, e `0.1 + 0.2` não é
 * `0.3` em lugar nenhum.
 */
export function dinheiro(centavos: number | undefined): string {
  if (centavos === undefined) return "—";
  return (centavos / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

/**
 * Data curta de um INSTANTE, no fuso do operador: `26/08/2026`.
 *
 * Vencimento não passa por aqui. Ele é dia de calendário, a API o escreve como meia-noite
 * UTC, e no fuso de Brasília isso ainda é o dia anterior: a tela diria que o boleto vence
 * um dia antes do que a ficha imprime. Use `dia`.
 */
export function data(iso: string | undefined): string {
  if (!iso) return "—";
  const instante = new Date(iso);
  if (Number.isNaN(instante.getTime())) return "—";
  return instante.toLocaleDateString("pt-BR");
}

/** Data e hora: `26/08/2026 14:32`. Para "atualizado em" e linha do tempo. */
export function dataHora(iso: string | undefined): string {
  if (!iso) return "—";
  const instante = new Date(iso);
  if (Number.isNaN(instante.getTime())) return "—";
  return `${instante.toLocaleDateString("pt-BR")} ${instante.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

/**
 * Quanto tempo faz, em texto curto: `3 min`, `2 h`, `5 d`.
 *
 * O operador lê a lista procurando o que está parado. "há 4 h" responde isso
 * mais rápido que um carimbo de data que ele precisa subtrair de cabeça.
 */
export function faz(iso: string | undefined): string {
  if (!iso) return "—";
  const instante = new Date(iso);
  if (Number.isNaN(instante.getTime())) return "—";

  const segundos = Math.floor((Date.now() - instante.getTime()) / 1000);
  if (segundos < 0) return "agora";
  if (segundos < 60) return `${segundos}s`;

  const minutos = Math.floor(segundos / 60);
  if (minutos < 60) return `${minutos} min`;

  const horas = Math.floor(minutos / 60);
  if (horas < 48) return `${horas} h`;

  return `${Math.floor(horas / 24)} d`;
}

/**
 * Aplica máscara de CPF ou CNPJ, numérico ou alfanumérico.
 *
 * **Só formata o que já está completo.** Um documento mascarado pela API
 * (`529******25`) tem asteriscos no meio, e tentar mascará-lo produziria lixo —
 * então ele passa direto, como veio. O CNPJ alfanumérico tem doze posições de
 * `0-9A-Z` e dois dígitos verificadores numéricos.
 */
export function documento(bruto: string | undefined): string {
  if (!bruto) return "—";

  if (/^\d{11}$/.test(bruto)) {
    return bruto.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  if (/^[0-9A-Z]{12}\d{2}$/.test(bruto)) {
    return bruto.replace(/(.{2})(.{3})(.{3})(.{4})(.{2})/, "$1.$2.$3/$4-$5");
  }
  return bruto;
}

/** Tamanho de cada bloco, com o separador que vem antes do bloco seguinte. */
const BLOCOS_DO_CPF = [[3, "."], [3, "."], [3, "-"], [2, ""]] as const;
const BLOCOS_DO_CNPJ = [[2, "."], [3, "."], [3, "/"], [4, "-"], [2, ""]] as const;

/**
 * A máscara de DIGITAÇÃO do documento: pontua o que já foi digitado, sem esperar
 * o documento completo.
 *
 * Não confere dígito, de propósito: quem decide o que é um documento válido é
 * `domain.VerificarDocumento`, e uma segunda verificação aqui divergiria dela
 * (`docs/HANDOFF-painel-e-cadastro.md:166`). Letra só existe em CNPJ, e minúscula
 * vira maiúscula porque a API a normaliza do mesmo jeito.
 */
export function mascaraDeDocumento(digitado: string): string {
  const limpo = digitado
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, "")
    .slice(0, 14);
  const blocos = limpo.length <= 11 && /^\d*$/.test(limpo) ? BLOCOS_DO_CPF : BLOCOS_DO_CNPJ;

  let mascarado = "";
  let inicio = 0;
  for (const [tamanho, separador] of blocos) {
    const bloco = limpo.slice(inicio, inicio + tamanho);
    mascarado += bloco;
    inicio += tamanho;
    if (limpo.length <= inicio) break;
    mascarado += separador;
  }
  return mascarado;
}

/**
 * Código FEBRABAN para nome de banco.
 *
 * Lista curta de propósito: o produto opera Sicoob e Sicredi predominantemente
 * (doc 06a §8). Um código desconhecido volta como está — mostrar `341` é
 * melhor que mostrar "Desconhecido", porque o número é pesquisável.
 */
const BANCOS: Record<string, string> = {
  "001": "Banco do Brasil",
  "033": "Santander",
  "104": "Caixa",
  "237": "Bradesco",
  "341": "Itaú",
  "748": "Sicredi",
  "756": "Sicoob",
};

export function banco(codigo: string | undefined): string {
  if (!codigo) return "—";
  const nome = BANCOS[codigo];
  return nome ? `${codigo} ${nome}` : codigo;
}

/** Encurta um UUID para leitura: `01a080b9…f36542`. O completo fica no title. */
export function idCurto(id: string | undefined): string {
  if (!id) return "—";
  if (id.length <= 16) return id;
  return `${id.slice(0, 8)}…${id.slice(-6)}`;
}

/**
 * Dia de calendário (`2026-11-01`) para leitura: `01/11/2026`.
 *
 * Sem `new Date`: a vigência de contrato é um DIA, e não um instante.
 * `new Date("2026-11-01")` é meia-noite UTC, que no fuso de Brasília ainda é
 * 31/10 — e a tela diria que o contrato começa um dia antes do gravado.
 */
export function dia(valor: string | null | undefined): string {
  if (!valor) return "—";
  const partes = /^(\d{4})-(\d{2})-(\d{2})/.exec(valor);
  if (partes === null) return valor;
  return `${partes[3]}/${partes[2]}/${partes[1]}`;
}

/** Quantidade de boletos, com separador de milhar: `1.000`. */
export function quantidade(valor: number): string {
  return valor.toLocaleString("pt-BR");
}

/** `1 fatura`, `12 faturas`. Plural com parênteses é texto de formulário de cartório. */
export function contagem(valor: number, singular: string, plural: string): string {
  return `${quantidade(valor)} ${valor === 1 ? singular : plural}`;
}

const UM_DIA_EM_MS = 86_400_000;

/** Dias de calendário entre dois instantes, no fuso do operador: 23h e 1h distam um dia. */
function diasDeCalendario(de: Date, ate: Date): number {
  const inicio = Date.UTC(de.getFullYear(), de.getMonth(), de.getDate());
  const fim = Date.UTC(ate.getFullYear(), ate.getMonth(), ate.getDate());
  return Math.round((fim - inicio) / UM_DIA_EM_MS);
}

/**
 * Um vencimento perto de agora: `Vence em 8 meses`, `Venceu há 2 dias`, `Vence hoje`.
 *
 * Em dias até 30, que é a janela de `vencendo`, e em meses de 30 dias depois dela: quem
 * olha um certificado quer a ordem de grandeza, e a data exata fica na `Dica`.
 */
export function vencimentoRelativo(iso: string | null | undefined, agora = new Date()): string {
  if (!iso) return "—";
  const instante = new Date(iso);
  if (Number.isNaN(instante.getTime())) return "—";

  const passou = instante.getTime() < agora.getTime();
  const dias = Math.abs(diasDeCalendario(agora, instante));
  if (dias === 0) return passou ? "Venceu hoje" : "Vence hoje";

  const distancia =
    dias <= 30 ? contagem(dias, "dia", "dias") : contagem(Math.floor(dias / 30), "mês", "meses");
  return passou ? `Venceu há ${distancia}` : `Vence em ${distancia}`;
}

/**
 * Competência (`2026-10`) para leitura: `10/2026`.
 *
 * Sem `new Date`, pelo mesmo motivo do `dia`: a competência é um mês de
 * calendário UTC, e o dia 1º à meia-noite UTC ainda é o mês anterior no fuso
 * de Brasília.
 */
export function competencia(valor: string | null | undefined): string {
  if (!valor) return "—";
  // A rota da aplicação manda o primeiro dia do mês, e a do vínculo manda só o mês.
  const partes = /^(\d{4})-(\d{2})(?:-\d{2})?$/.exec(valor);
  if (partes === null) return valor;
  return `${partes[2]}/${partes[1]}`;
}
